import { useCallback, useMemo, useState } from 'react';

import { Menu, MenuItem } from '@mui/material';
import { usePostHog } from 'posthog-js/react';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import ExportButton from '@/components/Chart/ExportButton';
import type {
  ChartDownloadProperties,
  ChartParentComponent
} from '@/components/Chart/hooks/options/useChartFunctions';
import { useAppCsvExport } from '@/components/Chart/hooks/useAppCsvExport';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { usePageInformationContext } from '@/context/PageInformationContext';

import type { ExportingMimeTypeValue } from 'highcharts';

const defaultProperties: ChartDownloadProperties = {
  format: '',
  fullScreen: false,
  darkMode: true,
  chartLocation: null
};

const CustomDownloadChartButton = ({
  tabName,
  disabled = false
}: {
  tabName: string;
  disabled?: boolean;
}) => {
  const { handleExport: handleCsvExport, exportDisabled } = useAppCsvExport(tabName, disabled);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { formatDateString } = useDateText();
  const { chartRef, chartSeries } = useChartContext();
  const postHog = usePostHog();

  // @ts-expect-error can be html
  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const getDateSuffix = useCallback(() => {
    const date = formatDateString(new Date().toJSON(), DateTextVariants.WITH_TIME);
    return date.split(' ').join('T');
  }, [formatDateString]);

  const onExport = useCallback(
    (type: ExportingMimeTypeValue) => {
      if (chartRef.current?.chart) {
        chartRef.current.chart.exportChart(
          { type, filename: `GX-Chart-${getDateSuffix()}` },
          chartRef.current.chart.options.exporting?.chartOptions ?? chartRef.current.chart.options
        );
      }
    },
    [chartRef, getDateSuffix]
  );

  const { mainPageTitle, subPageTitle } = usePageInformationContext();
  const chartParent: ChartParentComponent = useMemo(
    () => ({
      mainPageTitle,
      subPageTitle,
      tabName
    }),
    [mainPageTitle, subPageTitle, tabName]
  );
  const captureChartDownloadAction = useCallback(
    (format: string) => {
      const properties = {
        format,
        fullScreen: true,
        darkMode: false,
        chartLocation: chartParent
      };
      const chartSymbols = chartSeries?.map(s => {
        const metadata = s.custom;
        if (metadata) {
          return {
            Code: metadata.Code,
            Period: metadata.Period,
            PeriodType: metadata.PeriodType,
            TimeRef: metadata.TimeRef
          };
        }
        return undefined;
      });

      const mergedProperties: ChartDownloadProperties = { ...defaultProperties, ...properties };

      postHog?.capture('chart_download_button_click', {
        symbols: chartSymbols,
        ...mergedProperties
      });
    },
    [chartParent, chartSeries, postHog]
  );

  const handlePNGExport = () => {
    captureChartDownloadAction('PNG');
    onExport('image/png');
    handleClose();
  };

  const handleJPEGExport = () => {
    captureChartDownloadAction('JPEG');
    onExport('image/jpeg');
    handleClose();
  };

  const handlePDFExport = () => {
    captureChartDownloadAction('PDF');
    onExport('application/pdf');
    handleClose();
  };

  const handleSVGExport = () => {
    captureChartDownloadAction('SVG');
    onExport('image/svg+xml');
    handleClose();
  };

  return (
    <>
      <ExportButton
        disabled={disabled}
        handleClick={handleClick}
        title='Export chart'
      />
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        id='chart-export-menu'
        open={open}
        sx={{ zIndex: 9999999 }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        onClose={handleClose}
      >
        <MenuItem
          sx={{ fontSize: 12, fontWeight: 600 }}
          onClick={handlePNGExport}
        >
          Download PNG image
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 12, fontWeight: 600 }}
          onClick={handleJPEGExport}
        >
          Download JPEG image
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 12, fontWeight: 600 }}
          onClick={handlePDFExport}
        >
          Download PDF document
        </MenuItem>
        <MenuItem
          sx={{ fontSize: 12, fontWeight: 600 }}
          onClick={handleSVGExport}
        >
          Download SVG vector image
        </MenuItem>
        {!exportDisabled && (
          <MenuItem
            sx={{ fontSize: 12, fontWeight: 600 }}
            onClick={handleCsvExport}
          >
            Download as CSV
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default CustomDownloadChartButton;
